/**
 * @file: inspectedItemToReportItem.ts
 * @copyright: (c) 2019-2021 sichuan zhichetech co., ltd.
 */

import { InspectionSiteInfo } from '@euler/model';
import { AbnormalLevel, SeverityLevel } from '@euler/model/enum';
import {
  InspectionReportItem,
  InspectionReportItemType,
  MediaInfo,
  MediaSourceType,
} from '@euler/model/report';
import { SiteInspection, SiteInspectionItem } from '@euler/model/task-detail';
import { dateFromValue } from '@euler/utils/datetime';

export const inspectedItemToReportItem = (params: {
  site?: InspectionSiteInfo;
  inspectedSite: SiteInspection;
  inspectedItem: SiteInspectionItem;
}): InspectionReportItem => {
  const { site, inspectedSite, inspectedItem } = params;
  const item = site?.checkItems.find(x => x.id === inspectedItem.itemId);
  const optionsOfItem = item?.options ?? [];

  const normalOption = optionsOfItem.find(
    x =>
      x.abnormalLevel === AbnormalLevel.Fine &&
      x.severityLevel === SeverityLevel.None,
  );

  let abnormalOption = optionsOfItem.find(
    x =>
      x.abnormalLevel === inspectedItem.abnormalLevel &&
      x.severityLevel === inspectedItem.severityLevel,
  );

  const abnormalOptionWithDescription = optionsOfItem.find(
    x => x.abnormalLevel !== AbnormalLevel.Fine && x.description,
  );

  const isNormal = inspectedItem.abnormalLevel === AbnormalLevel.Fine;

  if (!abnormalOption && !isNormal) {
    // temporary fake option for later use
    abnormalOption = {
      id: 0,
      itemId: inspectedItem.itemId,
      label: inspectedItem.optionLabel!,
      labelFormat: inspectedItem.optionLabelFormat,
      lower: inspectedItem.optionLower,
      lowerInclusive: inspectedItem.optionLowerInclusive,
      upper: inspectedItem.optionUpper,
      upperInclusive: inspectedItem.optionUpperInclusive,
      maintenanceAdvice: inspectedItem.maintenanceAdvice,
      abnormalLevel: inspectedItem.abnormalLevel,
      severityLevel: inspectedItem.severityLevel,
      isPicPreferred: false,
    };
  }

  return {
    type: InspectionReportItemType.Normal,
    label: inspectedItem.label,
    inspectionType: inspectedSite.inspectionType,
    siteId: inspectedSite.siteId,
    siteName: inspectedSite.name,
    siteInfluenceFactor: 0,
    siteItemInfluenceFactor: 0,
    siteItemId: inspectedItem.itemId,
    siteItemName: inspectedItem.name,
    abnormalLevel: inspectedItem.abnormalLevel,
    severityLevel: inspectedItem.severityLevel,
    maintenanceAdvice: inspectedItem.maintenanceAdvice,
    resultDataType: inspectedItem.resultDataType,
    resultDataStringValue: inspectedItem.resultDataStringValue,
    resultDataNumberValue: inspectedItem.resultDataNumberValue,
    resultDataUnit: inspectedItem.resultDataUnit,
    resultRemark: inspectedItem.remark,
    normalValueType: item?.valueType,
    normalValueUnit: item?.valueUnit,
    normalResultLabel: normalOption?.label,
    normalResultLabelFormat: normalOption?.labelFormat,
    normalResultLower: normalOption?.lower,
    normalResultUpper: normalOption?.upper,
    normalResultLowerInclusive: normalOption?.lowerInclusive,
    normalResultUpperInclusive: normalOption?.upperInclusive,
    abnormalResultLabel: abnormalOption?.label,
    abnormalResultLabelFormat: abnormalOption?.labelFormat,
    abnormalResultLower: abnormalOption?.lower,
    abnormalResultLowerInclusive: abnormalOption?.lowerInclusive,
    abnormalResultUpper: abnormalOption?.upper,
    abnormalResultUpperInclusive: abnormalOption?.upperInclusive,
    abnormalDescription: abnormalOptionWithDescription?.description,
    data: [],
    medias: inspectedItem.medias.map<MediaInfo>(x => ({
      id: x.cid,
      type: x.type,
      sourceType: MediaSourceType.Inspection,
      category: '',
      title: x.title,
      subTitle: x.subTitle,
      url: x.url,
      coverUrl: x.coverUrl,
      dateRef: dateFromValue(x.dateRef!),
      remark: x.remark,
      annotationMetadata: x.annotationMetadata,
    })),
  };
};
